import React from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';

interface StepIndicatorProps {
    steps: string[];
    activeStep: number;
    setActiveStep?: (step: number) => void;
}

interface StepLineIndicatorProps {
    steps: number;
    activeStep: number;
    width?: string | number;
    stepNames?: string[];
    forceCompletedSteps?: number[];
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, activeStep, setActiveStep }) => {
    return (
        <Box display="flex" alignItems="center" gap={2}>
            {steps.map((step, idx) => {
                const isActive = idx === activeStep;
                const isCompleted = idx < activeStep;
                return (
                    <Box
                        key={step}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        onClick={() => setActiveStep?.(idx)}
                        sx={{ cursor: setActiveStep ? 'pointer' : 'default' }}
                    >
                        <Typography
                            color={isActive || isCompleted ? "var(--color-default-text)" : "#d3d3d3"}
                            fontSize={14}
                            sx={{ mb: 0.5 }}
                        >
                            {step}
                        </Typography>
                        <Box
                            sx={{
                                width: 80,
                                height: 6,
                                borderRadius: 3,
                                background: isActive || isCompleted ? "var(--color-primary)" : "#ededed",
                                opacity: isActive || isCompleted ? 1 : 0.5,
                            }}
                        />
                    </Box>
                );
            })}
        </Box>
    );
};

export const StepLineIndicator: React.FC<StepLineIndicatorProps> = ({ steps, activeStep, width = '100%', stepNames, forceCompletedSteps = [] }) => {
    return (
        <Box display="flex" flexDirection="column" width={width} boxSizing="border-box">
            <Box display="flex" alignItems="center" px={2}>
                {[...Array(steps)].map((_, idx) => {
                    const isCompleted = idx < activeStep || forceCompletedSteps.includes(idx);
                    const isActive = idx === activeStep;
                    return (
                        <React.Fragment key={idx}>
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    border: isActive ? '2px solid #00b59e' : isCompleted ? 'none' : '2px solid #e0e2e6',
                                    background: isCompleted ? 'var(--color-primary)' : '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    zIndex: 2,
                                }}
                            >
                            {isCompleted ? (
                                <CheckIcon sx={{ color: '#fff', fontSize: 20 }} />
                            ) : (
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: isActive ? 'var(--color-primary)' : '#e0e2e6',
                                        position: 'relative',
                                    }}
                                />
                            )}
                            </Box>
                            {idx < steps - 1 && (
                            <Box
                                sx={{
                                    flex: 1,
                                    height: 4,
                                    background: idx < activeStep ? 'var(--color-primary)' : '#e0e2e6',
                                    zIndex: 1,
                                    position: 'relative',
                                }}
                            />
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>
            {stepNames && stepNames.length > 0 && (
                <Box display="flex" alignItems="flex-start" px={2} mt={0.5}>
                    {[...Array(steps)].map((_, idx) => (
                        <React.Fragment key={idx}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    zIndex: 2,
                                    px: 0.5,
                                    minWidth: 0,
                                    flexShrink: 1,
                                }}
                            >
                                {stepNames[idx] && (
                                    <Typography
                                        fontSize={10}
                                        color="var(--color-text-muted)"
                                        sx={{ fontWeight: activeStep === idx ? 600 : 400, textAlign: 'center', whiteSpace: 'normal', lineHeight: 1.2, wordBreak: 'break-word' }}
                                    >
                                        {stepNames[idx]}
                                    </Typography>
                                )}
                            </Box>
                            {idx < steps - 1 && (
                                <Box
                                    sx={{
                                        flex: 1,
                                        zIndex: 1,
                                        position: 'relative',
                                    }}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default StepIndicator;
